import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import toast from 'react-hot-toast'
import { RefreshCw } from 'lucide-react'
import {
  clearResourceStatus, explainResourceLimits, getResourceLimits, getResourceStatus,
  reconcileResourceLimits, updateResourceLimits,
  type PlanOverCeiling, type ResourceJobState, type ResourceLimitCatalog,
  type ResourceLimitExplain, type ResourceStatus, type ResourceStatusRow,
} from '@/api/instanceAdmin'
import { Field, InfoBox, Section, Td, Th, chipStyle, errText, tableStyle } from './adminUi'

/**
 * Instance Admin → Resource limits.
 *
 * What one application may use of the shared server: the instance-wide
 * ceilings no hosting plan can exceed, how a plan's per-user allowance counts
 * users, what the watchdog does to an application that keeps going over, and
 * the live state it keeps per application.
 */

const USER_BASIS_LABELS: Record<string, string> = {
  provisioned: 'Users included in the plan',
  registered: 'People holding a role on the app',
  active: 'Active this month',
}

const WATCHDOG_LABELS: Record<string, string> = {
  alert: 'Alert only — email the addresses below, change nothing',
  throttle: 'Throttle — slow the application down for a while, and alert',
  suspend: 'Suspend — refuse new work until an administrator clears it, and alert',
}

const DECIDED_BY: Record<string, string> = {
  plan: 'Plan',
  plan_max: 'Plan maximum',
  ceiling: 'Instance ceiling',
  min: 'Minimum',
  default: 'Default',
}

const STATE_COLOR: Record<string, string> = {
  ok: 'var(--color-success)',
  alerted: 'var(--color-warning)',
  throttled: 'var(--color-warning)',
  suspended: 'var(--color-error)',
}

const hintStyle: CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5,
}

function fmtWhen(v: string | null | undefined): string {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString()
}

export default function ResourceLimitsPanel() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
      <PolicyCard />
      <StatusCard />
      <ExplainCard />
    </div>
  )
}

/* ── Ceilings + scaling + watchdog (one Save) ─────────────────────── */

function PolicyCard() {
  const [catalog, setCatalog] = useState<ResourceLimitCatalog | null>(null)
  // STRING state per ceiling: blank = the catalog ceiling, and must stay blank.
  const [ceilings, setCeilings] = useState<Record<string, string>>({})
  const [userBasis, setUserBasis] = useState('provisioned')
  const [action, setAction] = useState('alert')
  const [emails, setEmails] = useState('')
  const [overCeiling, setOverCeiling] = useState<PlanOverCeiling[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    getResourceLimits()
      .then((d) => {
        setCatalog(d.catalog)
        const c: Record<string, string> = {}
        for (const [k, v] of Object.entries(d.policy.ceilings || {})) c[k] = String(v)
        setCeilings(c)
        setUserBasis(d.policy.user_basis || 'provisioned')
        setAction(d.policy.watchdog_action || 'alert')
        setEmails((d.policy.alert_emails || []).join(', '))
        setOverCeiling(d.plans_over_ceiling || [])
      })
      .catch((e) => toast.error(errText(e, 'Failed to load the resource limits')))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>
  if (!catalog) return <div style={{ color: 'var(--color-error)' }}>The resource limits could not be loaded.</div>

  const save = async () => {
    const out: Record<string, number> = {}
    for (const [k, raw] of Object.entries(ceilings)) {
      const t = raw.trim()
      if (!t) continue
      const n = Number(t)
      if (!Number.isFinite(n) || n < 0) {
        setError(`"${catalog.limits[k]?.label || k}" must be a number of 0 or more`)
        return
      }
      out[k] = n
    }
    setSaving(true)
    setError('')
    try {
      const res = await updateResourceLimits({
        ceilings: out,
        user_basis: userBasis,
        watchdog_action: action,
        alert_emails: emails.split(',').map((s) => s.trim()).filter(Boolean),
      })
      setOverCeiling(res.plans_over_ceiling || [])
      toast.success('Resource limits saved')
    } catch (e) {
      const msg = errText(e, 'Failed to save the resource limits')
      setError(msg)
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <Section title="Global ceilings">
        <InfoBox>
          No hosting plan can exceed these values. Leave a value blank to use the built-in
          ceiling. Instance-wide settings (marked "whole server") apply to everyone as they are.
        </InfoBox>
        {catalog.groups.map((g) => {
          const keys = Object.keys(catalog.limits).filter((k) => catalog.limits[k].group === g.id)
          if (!keys.length) return null
          return (
            <div key={g.id} style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{g.label}</div>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>Limit</Th>
                    <Th right>Default</Th>
                    <Th right>Ceiling</Th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => {
                    const d = catalog.limits[k]
                    return (
                      <tr key={k}>
                        <Td>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span>{d.label}</span>
                            {d.scope === 'global' && <span style={chipStyle}>whole server</span>}
                          </div>
                          {d.hint && <div style={hintStyle}>{d.hint}</div>}
                        </Td>
                        <Td right style={{ whiteSpace: 'nowrap' }}>
                          {d.default.toLocaleString()} {d.unit}
                        </Td>
                        <Td right style={{ whiteSpace: 'nowrap' }}>
                          <input
                            type="number"
                            min={d.min}
                            value={ceilings[k] ?? ''}
                            placeholder={String(d.ceiling)}
                            onChange={(e) => setCeilings((c) => ({ ...c, [k]: e.target.value }))}
                            style={{ width: 110 }}
                          />
                          <span style={{ marginLeft: '0.4rem', color: 'var(--color-text-secondary)' }}>{d.unit}</span>
                        </Td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )
        })}
      </Section>

      <Section title="Scaling & watchdog">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', maxWidth: 560 }}>
          <Field label="Count users as" hint="A plan's per-user allowance is multiplied by this number.">
            <select value={userBasis} onChange={(e) => setUserBasis(e.target.value)} style={{ width: '100%' }}>
              {(catalog.user_basis.length ? catalog.user_basis : Object.keys(USER_BASIS_LABELS)).map((b) => (
                <option key={b} value={b}>{USER_BASIS_LABELS[b] || b}</option>
              ))}
            </select>
          </Field>
          <Field label="When an application keeps going over its limits">
            <select value={action} onChange={(e) => setAction(e.target.value)} style={{ width: '100%' }}>
              {(catalog.watchdog_actions.length ? catalog.watchdog_actions : Object.keys(WATCHDOG_LABELS)).map((a) => (
                <option key={a} value={a}>{WATCHDOG_LABELS[a] || a}</option>
              ))}
            </select>
          </Field>
          <Field label="Alert emails" hint="Comma-separated. Instance administrators to notify.">
            <input value={emails} onChange={(e) => setEmails(e.target.value)}
                   placeholder="ops@example.com, admin@example.com" style={{ width: '100%' }} />
          </Field>
        </div>
      </Section>

      {error && <div style={{ color: 'var(--color-error)', fontSize: '0.875rem' }}>{error}</div>}

      {overCeiling.length > 0 && (
        <div style={{
          fontSize: '0.875rem', border: '1px solid var(--color-warning)', borderRadius: 6,
          padding: '0.75rem 0.875rem', lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
            These plans have values above the new ceiling; they are capped automatically.
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {overCeiling.map((p) => (
              <li key={p.code}>
                <strong>{p.display_name}</strong> ({p.code}): {p.problems.join('; ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save resource limits'}
        </button>
      </div>
    </div>
  )
}

/* ── Live status ──────────────────────────────────────────────────── */

function JobLine({ label, job }: { label: string; job?: ResourceJobState }) {
  return (
    <div style={hintStyle}>
      <strong style={{ color: 'var(--color-text)' }}>{label}:</strong>{' '}
      {job
        ? <>last run {fmtWhen(job.last_run_at)} · {job.last_status || 'unknown'}
            {job.detail?.error ? <span style={{ color: 'var(--color-error)' }}> · {job.detail.error}</span> : null}</>
        : 'has not run yet'}
    </div>
  )
}

function StatusCard() {
  const [status, setStatus] = useState<ResourceStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [clearing, setClearing] = useState<string | null>(null)
  const [reconciling, setReconciling] = useState(false)
  const [reconcileResult, setReconcileResult] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setStatus(await getResourceStatus())
    } catch (e) {
      toast.error(errText(e, 'Failed to load the live status'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const rowKey = (r: ResourceStatusRow) => `${r.owner_user_id}|${r.project_id}|${r.tenant_id}`

  const clear = async (r: ResourceStatusRow) => {
    setClearing(rowKey(r))
    try {
      await clearResourceStatus({
        owner_user_id: r.owner_user_id, project_id: r.project_id, tenant_id: r.tenant_id,
      })
      toast.success('Cleared')
      await load()
    } catch (e) {
      toast.error(errText(e, 'Failed to clear'))
    } finally {
      setClearing(null)
    }
  }

  const reconcile = async () => {
    setReconciling(true)
    setReconcileResult('')
    try {
      const r = await reconcileResourceLimits()
      setReconcileResult(
        `${r.tenants} organization${r.tenants === 1 ? '' : 's'} checked, ${r.changed} updated`
        + (r.failed ? `, ${r.failed} failed` : ''))
      await load()
    } catch (e) {
      toast.error(errText(e, 'Failed to re-apply the database limits'))
    } finally {
      setReconciling(false)
    }
  }

  const rows = status?.rows || []

  return (
    <Section title="Live status">
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <button className="btn btn-secondary" onClick={reconcile} disabled={reconciling}>
          {reconciling ? 'Applying…' : 'Re-apply database limits now'}
        </button>
        {reconcileResult && <span style={hintStyle}>{reconcileResult}</span>}
      </div>
      <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <JobLine label="Watchdog" job={status?.jobs?.resource_watchdog} />
        <JobLine label="Database limits" job={status?.jobs?.resource_role_reconcile} />
      </div>

      {status && rows.length === 0 && (
        <div style={{ ...hintStyle, marginTop: '0.875rem' }}>
          No application has gone over its limits.
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Application</Th>
                <Th>Organization</Th>
                <Th>State</Th>
                <Th right>Refused</Th>
                <Th right>Busy</Th>
                <Th right>Queries stopped</Th>
                <Th right>Sessions ended</Th>
                <Th>Running now</Th>
                <Th>Reason</Th>
                <Th>Slowed until</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={rowKey(r)}>
                  <Td>
                    {r.tenant_wide ? <em>Whole organization</em> : r.project_id}
                    <div style={hintStyle}>{r.owner_user_id}</div>
                  </Td>
                  <Td>{r.tenant_id || '—'}</Td>
                  <Td>
                    <span style={{ ...chipStyle, color: STATE_COLOR[r.state] || chipStyle.color, borderColor: STATE_COLOR[r.state] }}>
                      {r.state}
                    </span>
                  </Td>
                  <Td right>{r.rejected_requests ?? 0}</Td>
                  <Td right>{r.busy_refusals ?? 0}</Td>
                  <Td right>{r.cancelled_queries ?? 0}</Td>
                  <Td right>{r.terminated_sessions ?? 0}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>
                    {r.running
                      ? `${r.running.report} reports · ${r.running.process} processes · ${r.running.import} imports · ${r.running.background} background`
                      : '—'}
                  </Td>
                  <Td>{r.reason || '—'}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.throttled_until)}</Td>
                  <Td>
                    {r.state !== 'ok' && (
                      <button className="btn btn-secondary" onClick={() => clear(r)}
                              disabled={clearing === rowKey(r)}>
                        {clearing === rowKey(r) ? 'Clearing…' : 'Clear'}
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

/* ── Explain one application's effective limits ───────────────────── */

function ExplainCard() {
  const [owner, setOwner] = useState('')
  const [project, setProject] = useState('')
  const [tenant, setTenant] = useState('')
  const [result, setResult] = useState<ResourceLimitExplain | null>(null)
  const [catalog, setCatalog] = useState<ResourceLimitCatalog | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const explain = async () => {
    if (!owner.trim() || !project.trim()) {
      setError('Enter the owner user id and the application name')
      return
    }
    setBusy(true)
    setError('')
    try {
      // The catalog gives each key its label and unit; fetched once.
      const cat = catalog ?? (await getResourceLimits()).catalog
      setCatalog(cat)
      setResult(await explainResourceLimits({
        owner_user_id: owner.trim(), project: project.trim(),
        ...(tenant.trim() ? { tenant_id: tenant.trim() } : {}),
      }))
    } catch (e) {
      setResult(null)
      setError(errText(e, 'Could not work out the limits for that application'))
    } finally {
      setBusy(false)
    }
  }

  const keys = useMemo(() => Object.keys(result?.limits || {}), [result])

  return (
    <Section title="Explain an application">
      <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', alignItems: 'end' }}>
        <Field label="Owner user id">
          <input value={owner} onChange={(e) => setOwner(e.target.value)} style={{ width: '100%' }} />
        </Field>
        <Field label="Application">
          <input value={project} onChange={(e) => setProject(e.target.value)} style={{ width: '100%' }} />
        </Field>
        <Field label="Organization id (optional)">
          <input value={tenant} onChange={(e) => setTenant(e.target.value)} style={{ width: '100%' }} />
        </Field>
        <div>
          <button className="btn btn-primary" onClick={explain} disabled={busy}>
            {busy ? 'Working…' : 'Explain'}
          </button>
        </div>
      </div>
      {error && <div style={{ color: 'var(--color-error)', fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</div>}
      {result && (
        <>
          <div style={{ ...hintStyle, marginTop: '0.875rem' }}>
            Plan <strong style={{ color: 'var(--color-text)' }}>{result.plan?.display_name || result.plan?.code || '—'}</strong>
            {' · '}{result.users} user{result.users === 1 ? '' : 's'} counted as
            {' '}"{USER_BASIS_LABELS[result.user_basis] || result.user_basis}"
            {result.tenant ? <> · organization {result.tenant}</> : null}
          </div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Limit</Th>
                <Th right>Effective value</Th>
                <Th>Decided by</Th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const e = result.limits[k]
                const d = catalog?.limits[k]
                return (
                  <tr key={k}>
                    <Td>{d?.label || k}</Td>
                    <Td right style={{ whiteSpace: 'nowrap' }}>{e.value.toLocaleString()} {d?.unit || ''}</Td>
                    <Td>{DECIDED_BY[e.capped_by] || e.capped_by}</Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </Section>
  )
}
